import { googleFonts } from "../../../lib/fonts";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import type { VideoFormat } from "@/lib/hooks/useImageUpload";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Loader2 } from "lucide-react";
import { WatermarkPreview } from "./WatermarkPreview";
import { type WatermarkSettings, getContrastColor, getContainerAlignment } from "./video-form-utils";
import { useState, useEffect, Fragment } from "react";

// Sub-components
import { BrandingChoice } from "./watermark-step/BrandingChoice";
import { TemplateManagement } from "./watermark-step/TemplateManagement";
import { LogoControls } from "./watermark-step/LogoControls";
import { TitleControls } from "./watermark-step/TitleControls";
import { BrandingFooter } from "./watermark-step/BrandingFooter";
import { BrandingSkipped } from "./watermark-step/BrandingSkipped";

interface WatermarkStepProps {
  watermarkChoice: "yes" | "no" | null;
  setWatermarkChoice: (choice: "yes" | "no") => void;
  handleRemoveWatermark: () => void;
  watermarkPreview: string | null;
  templates: any[];
  handleSelectTemplate: (template: any) => void;
  isCustomLogo: boolean | null;
  setIsSaveTemplateDialogOpen: (open: boolean) => void;
  watermarkUrl: string | null;
  formats: VideoFormat[];
  watermarkSettingsByFormat: Record<VideoFormat, WatermarkSettings>;
  setWatermarkSettingsByFormat: React.Dispatch<
    React.SetStateAction<Record<VideoFormat, WatermarkSettings>>
  >;
  getContainerAlignment: (alignment: string) => React.CSSProperties;
  handleWatermarkUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  prevStep: () => void;
  quota: any;
  canSubmit?: boolean;
  isSubmitting?: boolean;
  captureIntroClipRef?: React.RefObject<{ captureIntroClip: () => Promise<string[]> } | null>;
  activeFormat: VideoFormat;
  setActiveFormat: (format: VideoFormat) => void;
  activeTemplateId?: string | null;
  hasModifications?: boolean;
  handleUpdateTemplate?: () => void;
  isTemplateUpdating?: boolean;
}

export const WatermarkStep = ({
  watermarkChoice,
  setWatermarkChoice,
  handleRemoveWatermark,
  watermarkPreview,
  templates,
  handleSelectTemplate,
  isCustomLogo,
  setIsSaveTemplateDialogOpen,
  watermarkUrl,
  formats,
  watermarkSettingsByFormat,
  setWatermarkSettingsByFormat,
  getContainerAlignment,
  handleWatermarkUpload,
  prevStep,
  quota,
  canSubmit,
  isSubmitting,
  captureIntroClipRef,
  activeFormat,
  setActiveFormat,
  activeTemplateId,
  hasModifications,
  handleUpdateTemplate,
  isTemplateUpdating,
}: WatermarkStepProps) => {
  const { t } = useTranslation();

  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches
  );

  useEffect(() => {
    const check = () => setIsMobile(window.matchMedia('(max-width: 768px)').matches);
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const [activeBrandingTab, setActiveBrandingTab] = useState<'intro' | 'logo' | 'outro'>('intro');
  const [previewTrigger, setPreviewTrigger] = useState(0);
  const [stopTrigger, setStopTrigger] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);

  const brandingTabs = [
    { id: 'intro', label: t("createVideo.brandingTabs.intro") || "Opening Title" },
    { id: 'logo', label: t("createVideo.brandingTabs.logo") || "Logo" },
  ] as const;

  const introStylesList = [
    { id: 'cinematic', label: t("createVideo.introStyles.cinematic") || "Luxury" },
    { id: 'architect', label: t("createVideo.introStyles.architect") || "Structural" },
    { id: 'editorial', label: t("createVideo.introStyles.editorial") || "Elegant" },
    { id: 'geometric', label: t("createVideo.introStyles.geometric") || "Bold" },
    { id: 'typewriter', label: t("createVideo.introStyles.typewriter") || "Journal" },
  ] as const;

  const presetColors = [
    { name: "Black", value: "#000000" },
    { name: "White", value: "#FFFFFF" },
    { name: "Beige", value: "#F5F5DC" },
    { name: "Slate", value: "#4B5563" },
    { name: "Navy", value: "#1E3A8A" },
    { name: "Amber", value: "#B45309" },
    { name: "Crimson", value: "#991B1B" },
  ];

  const activeTemplate = templates.find((t) => t.id === activeTemplateId);

  return (
    <div className="space-y-12 animate-in fade-in slide-in-from-bottom-4 duration-500">
      {!watermarkChoice ? (
        <BrandingChoice 
          setWatermarkChoice={setWatermarkChoice} 
          prevStep={prevStep} 
          isMobile={isMobile}
          templatesCount={templates.length}
        />
      ) : (
        <div className="space-y-12">
          {watermarkChoice === "yes" ? (
            <div className="space-y-10">
              <div className="flex flex-col gap-6">
                <Label className="font-h3 text-xl 2k:text-4xl 4k:text-6xl">
                  {t("createVideo.watermarkLabel")}
                </Label>
                <p className="text-muted-foreground 2k:text-2xl 4k:text-4xl">
                  {t("createVideo.watermarkDescription")}
                </p>
              </div>

              <div className="space-y-10">
                <TemplateManagement 
                  templates={templates}
                  watermarkPreview={watermarkPreview}
                  handleWatermarkUpload={handleWatermarkUpload}
                  handleSelectTemplate={handleSelectTemplate}
                  isSubmitting={isSubmitting}
                  setIsSaveTemplateDialogOpen={setIsSaveTemplateDialogOpen}
                  watermarkUrl={watermarkUrl}
                  activeTemplate={activeTemplate}
                  handleUpdateTemplate={handleUpdateTemplate}
                  isMobile={isMobile}
                />


                {watermarkPreview && !isMobile && (
                  <div className="space-y-8">
                    {formats.length > 1 && (
                      <div className="flex bg-black/5 p-1.5 rounded-2xl w-fit">
                        {formats.map((format) => (
                          <button
                            key={format}
                            type="button"
                            onClick={() => {
                              setActiveFormat(format);
                              setPreviewTrigger(0);
                              setStopTrigger(Date.now());
                            }}
                            className={cn(
                              "px-8 py-3 rounded-xl text-sm font-bold transition-all",
                              activeFormat === format
                                ? "bg-white text-black shadow-sm"
                                : "text-muted-foreground hover:text-black",
                            )}
                          >
                            {format === "16:9"
                              ? t("createVideo.horizontal")
                              : t("createVideo.vertical")}
                          </button>
                        ))}
                      </div>
                    )}

                    <div className="space-y-8">
                      {formats
                        .filter((f) => f === activeFormat)
                        .map((format) => (
                          <Fragment key={format}>
                            <div
                              className="p-8 bg-white/50 rounded-[32px] border border-black/5 space-y-8"
                            >
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 items-start">
                              <div className="lg:col-span-2 space-y-6">
                                <WatermarkPreview
                                  aspectRatio={format}
                                  watermarkPreview={watermarkPreview}
                                  watermarkAlignment={watermarkSettingsByFormat[format].alignment}
                                  watermarkPadding={watermarkSettingsByFormat[format].padding}
                                  watermarkOpacity={watermarkSettingsByFormat[format].opacity}
                                  watermarkSize={watermarkSettingsByFormat[format].size}
                                  logoEnabled={watermarkSettingsByFormat[format].logoEnabled}
                                  introEnabled={watermarkSettingsByFormat[format].introEnabled}
                                  introAlignment={watermarkSettingsByFormat[format].introAlignment}
                                  introTitle={watermarkSettingsByFormat[format].introTitle}
                                  introStyle={watermarkSettingsByFormat[format].introStyle}
                                  introAnimation={watermarkSettingsByFormat[format].introAnimation}
                                  introBgColor={watermarkSettingsByFormat[format].introBgColor}
                                  introBgOpacity={watermarkSettingsByFormat[format].introBgOpacity}
                                  introTextColor={watermarkSettingsByFormat[format].introTextColor || "#FFFFFF"}
                                  introFont={watermarkSettingsByFormat[format].introFont}
                                  contactCard={watermarkSettingsByFormat[format].contactCard || ""}
                                  playTrigger={previewTrigger}
                                  stopTrigger={stopTrigger}
                                  onPlayStateChange={setIsPlaying}
                                  onIntroTitleChange={(title) => {
                                    setWatermarkSettingsByFormat((prev) => ({
                                      ...prev,
                                      [format]: { ...prev[format], introTitle: title },
                                    }));
                                  }}
                                  getContainerAlignment={getContainerAlignment}
                                  activeTab={activeBrandingTab}
                                  onContactCardChange={(html) =>
                                    setWatermarkSettingsByFormat((prev) => ({
                                      ...prev,
                                      [format]: { ...prev[format], contactCard: html },
                                    }))
                                  }
                                  captureIntroClipRef={captureIntroClipRef}
                                />
                              </div>

                              <div className="lg:col-span-1 space-y-8 animate-in fade-in slide-in-from-right-4 duration-500">
                                <div className="flex bg-black/[0.03] p-1.5 rounded-2xl w-full border border-black/5 shadow-inner backdrop-blur-sm">
                                  {brandingTabs.map((tab) => (
                                    <button
                                      key={tab.id}
                                      type="button"
                                      disabled={isPlaying}
                                      onClick={() => {
                                        setActiveBrandingTab(tab.id);
                                        setStopTrigger(Date.now());
                                      }}
                                      className={cn(
                                        "flex-1 py-3 px-2 flex items-center justify-center text-[10px] sm:text-xs font-bold rounded-xl transition-all duration-300 relative",
                                        activeBrandingTab === tab.id
                                          ? "bg-white text-black shadow-[0_2px_10px_rgba(0,0,0,0.1)] scale-[1.02]"
                                          : "text-muted-foreground hover:text-black hover:bg-black/[0.02]",
                                        isPlaying && "opacity-50 cursor-not-allowed grayscale"
                                      )}
                                    >
                                      <span className="truncate">{tab.label}</span>
                                    </button>
                                  ))}
                                </div>

                                {activeBrandingTab === "logo" && (
                                  <LogoControls 
                                    format={format}
                                    watermarkSettingsByFormat={watermarkSettingsByFormat}
                                    setWatermarkSettingsByFormat={setWatermarkSettingsByFormat}
                                    isPlaying={isPlaying}
                                    watermarkPreview={watermarkPreview}
                                    handleWatermarkUpload={handleWatermarkUpload}
                                    activeTemplate={activeTemplate}
                                    getContainerAlignment={getContainerAlignment}
                                  />
                                )}

                                {activeBrandingTab === "intro" && (
                                  <TitleControls 
                                    format={format}
                                    watermarkSettingsByFormat={watermarkSettingsByFormat}
                                    setWatermarkSettingsByFormat={setWatermarkSettingsByFormat}
                                    isPlaying={isPlaying}
                                    introStylesList={introStylesList}
                                    presetColors={presetColors}
                                    getContrastColor={getContrastColor}
                                    setStopTrigger={setStopTrigger}
                                    setPreviewTrigger={setPreviewTrigger}
                                    getContainerAlignment={getContainerAlignment}
                                    googleFonts={googleFonts}
                                  />
                                )}
                              </div>
                            </div>
                          </div>
                          {activeTemplate && hasModifications && (
                            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 bg-amber-50 border border-amber-200 rounded-xl gap-4 mt-6">
                              <div className="flex items-center gap-3 text-amber-800">
                                <AlertTriangle className="size-5 shrink-0" />
                                <p className="text-sm font-medium">{t("createVideo.unsavedChanges") || "You have unsaved changes to this template"}</p>
                              </div>
                              <Button 
                                type="button"
                                variant="outline" 
                                className="bg-white border-amber-200 text-amber-800 hover:bg-amber-100 hover:text-amber-900 w-full sm:w-auto font-medium gap-2"
                                onClick={handleUpdateTemplate}
                                disabled={isTemplateUpdating}
                              >
                                {isTemplateUpdating && <Loader2 className="size-4 animate-spin" />}
                                {t("createVideo.saveChanges") || "Save Changes"}
                              </Button>
                            </div>
                          )}
                        </Fragment>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <BrandingSkipped />
          )}

          <BrandingFooter 
            isPlaying={isPlaying}
            prevStep={prevStep}
            quota={quota}
            canSubmit={canSubmit}
            isSubmitting={isSubmitting}
          />
        </div>
      )}
    </div>
  );
};
